// ###### IMPORTS
import React, { useState } from 'react';
import { ExclamationTriangleIcon, CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { conflictResolver } from '../offline_items/conflictResolver';

// ###### COMPONENT
const ConflictResolutionModal = ({ 
  isOpen, 
  onClose, 
  conflict,
  onResolve 
}) => {
  const [selectedStrategy, setSelectedStrategy] = useState(conflictResolver.STRATEGIES.LAST_WRITE_WINS);
  const [isResolving, setIsResolving] = useState(false);

  if (!isOpen || !conflict) return null;

  const { localItem, serverItem, item, storeName } = conflict;
  
  // ##### FORMAT DATE HELPER
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // ##### GET ITEM DISPLAY NAME
  const getItemDisplayName = (item, storeName) => {
    if (!item) return 'Unknown item';
    
    // Try common fields
    if (item.title) return item.title;
    if (item.name) return item.name;
    if (item.account_name) return item.account_name;
    if (storeName === 'notes' && item.content) {
      return item.content.substring(0, 30) + (item.content.length > 30 ? '...' : '');
    }
    
    return `${storeName} #${item.id}`;
  };

  // ##### HANDLE RESOLVE
  const handleResolve = async () => {
    setIsResolving(true);
    try {
      await onResolve(conflict.item.id, selectedStrategy);
      onClose();
    } catch (error) {
      console.error('Failed to resolve conflict:', error);
    } finally {
      setIsResolving(false);
    }
  };

  const localName = getItemDisplayName(localItem, storeName);
  const serverName = getItemDisplayName(serverItem, storeName);
  const localDate = formatDate(localItem?.local_updated_at || localItem?.updated_at);
  const serverDate = formatDate(serverItem?.updated_at);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500" />
            <h2 className="text-xl font-semibold text-gray-800">Conflict Detected</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={isResolving}
          >
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Description */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              This item was modified both locally and on the server. Choose which version to keep.
            </p>
          </div>

          {/* Comparison */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Local Version */}
            <div className="border-2 border-blue-300 rounded-lg p-4 bg-blue-50">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <h3 className="font-semibold text-gray-800">Local Version</h3>
              </div>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-gray-600">Name:</span>
                  <p className="font-medium text-gray-800">{localName}</p>
                </div>
                <div>
                  <span className="text-gray-600">Last Modified:</span>
                  <p className="text-gray-700">{localDate}</p>
                </div>
                {localItem && (
                  <div className="mt-3 pt-3 border-t border-blue-200">
                    <details className="text-xs">
                      <summary className="cursor-pointer text-blue-600 hover:text-blue-700">
                        View details
                      </summary>
                      <pre className="mt-2 p-2 bg-white rounded text-xs overflow-auto max-h-32">
                        {JSON.stringify(localItem, null, 2)}
                      </pre>
                    </details>
                  </div>
                )}
              </div>
            </div>

            {/* Server Version */}
            <div className="border-2 border-green-300 rounded-lg p-4 bg-green-50">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <h3 className="font-semibold text-gray-800">Server Version</h3>
              </div>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-gray-600">Name:</span>
                  <p className="font-medium text-gray-800">{serverName}</p>
                </div>
                <div>
                  <span className="text-gray-600">Last Modified:</span>
                  <p className="text-gray-700">{serverDate}</p>
                </div>
                {serverItem && (
                  <div className="mt-3 pt-3 border-t border-green-200">
                    <details className="text-xs">
                      <summary className="cursor-pointer text-green-600 hover:text-green-700">
                        View details
                      </summary>
                      <pre className="mt-2 p-2 bg-white rounded text-xs overflow-auto max-h-32">
                        {JSON.stringify(serverItem, null, 2)}
                      </pre>
                    </details>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Resolution Strategy */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-800 mb-3">Resolution Strategy</h3>
            <div className="space-y-2">
              <label className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="radio"
                  name="strategy"
                  value={conflictResolver.STRATEGIES.LAST_WRITE_WINS}
                  checked={selectedStrategy === conflictResolver.STRATEGIES.LAST_WRITE_WINS}
                  onChange={(e) => setSelectedStrategy(e.target.value)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-800">Last Write Wins</div>
                  <div className="text-xs text-gray-600">
                    Keep the version that was modified most recently
                  </div>
                </div>
              </label>

              <label className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="radio"
                  name="strategy"
                  value={conflictResolver.STRATEGIES.SERVER_WINS}
                  checked={selectedStrategy === conflictResolver.STRATEGIES.SERVER_WINS}
                  onChange={(e) => setSelectedStrategy(e.target.value)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-800">Server Wins</div>
                  <div className="text-xs text-gray-600">
                    Always keep the server version (discard local changes)
                  </div>
                </div>
              </label>

              <label className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="radio"
                  name="strategy"
                  value={conflictResolver.STRATEGIES.CLIENT_WINS}
                  checked={selectedStrategy === conflictResolver.STRATEGIES.CLIENT_WINS}
                  onChange={(e) => setSelectedStrategy(e.target.value)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-800">Client Wins</div>
                  <div className="text-xs text-gray-600">
                    Always keep the local version (overwrite server)
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={isResolving}
            className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleResolve}
            disabled={isResolving}
            className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white shadow disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isResolving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Resolving...
              </>
            ) : (
              <>
                <CheckCircleIcon className="h-5 w-5" />
                Resolve Conflict
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConflictResolutionModal;
